import React, { useMemo } from "react";
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useTaskHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import AutoImportModal from "@/components/shared/modals/AutoImportModal";

// Task-specific components
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contact_Tasks } from "@/src/gql/graphql";

const TASK_CONFIG = getEntityTableConfig<Contact_Tasks>("tasks");
import { TaskTemplateSelector } from "@/features/tasks/components/TaskTemplateSelector";
import { SaveTemplateModal } from "@/features/tasks/components/SaveTemplateModal";
import type { CompanyTabComponentProps } from "@/lib/types/company";

// Types
import type { EntityFormProps } from "@/components/shared/UniversalEntityPage/types";

export const CompanyTasks: React.FC<CompanyTabComponentProps> = ({ companyId }) => {
  // Create hooks for tasks
  const taskHooks = useTaskHooks();

  // Create custom filters to filter tasks by company
  const customFilters = useMemo(
    () => ({
      // Filter tasks by company_id directly
      company_id: companyId,
    }),
    [companyId]
  );

  // Entity form props for template support
  const entityFormProps: EntityFormProps<Contact_Tasks> = useMemo(
    () => ({
      templateSelector: TaskTemplateSelector,
      saveTemplateModal: SaveTemplateModal,
    }),
    []
  );

  // Memoize custom components to prevent recreation
  // Note: We don't include taskHooks in dependencies because the hooks object
  // reference changes on every render, but the actual hook functions don't change.
  const customComponents = useMemo(
    () => ({
      importModal: (props: any) => (
        <AutoImportModal config={TASK_CONFIG} hooks={taskHooks} {...props} />
      ),
    }),
    []
  );

  return (
    <UniversalEntityPage
      entityName="tasks"
      config={TASK_CONFIG}
      hooks={taskHooks}
      filterPreset="all"
      title="Company Tasks"
      description={"View and manage tasks for this company"}
      storageKey="company-tasks-column-preferences"
      customFilters={customFilters}
      customComponents={customComponents}
      entityFormProps={entityFormProps}
      showHeader={false} // Hide header since it's in a tab
    />
  );
};
